import { signOut } from 'firebase/auth';
import { auth } from '../firebase/firebase';
import userStore from '../stores/UserStore';

const TOKEN_EXPIRED_CODES = new Set([
  'auth/user-token-expired',
  'auth/user-token-revoked',
  'auth/invalid-user-token',
  'auth/user-disabled',
]);

const UNAUTHENTICATED_CODES = new Set(['functions/unauthenticated', 'unauthenticated']);

let redirecting = false;

function errorCode(err: unknown): string {
  if (!err || typeof err !== 'object' || !('code' in err)) return '';
  return String((err as { code: unknown }).code);
}

/** True when Firebase or a callable rejected the current login, not a permissions or network failure. */
export function isSessionExpiredError(err: unknown): boolean {
  const code = errorCode(err);
  if (TOKEN_EXPIRED_CODES.has(code)) return true;
  if (UNAUTHENTICATED_CODES.has(code) && auth.currentUser) return true;
  return false;
}

/** Sign out and open the login screen. Safe to call more than once. */
export async function expireSessionAndGoToLogin(): Promise<void> {
  if (redirecting) return;
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/loginscreen')) return;
  if (!auth.currentUser && !userStore.user) return;

  redirecting = true;
  const from = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  try {
    await signOut(auth);
  } catch {
    /* already signed out */
  }
  userStore.clearUser();
  const params = new URLSearchParams({ session: 'expired' });
  if (from.startsWith('/') && !from.startsWith('//')) {
    params.set('from', from);
  }
  window.location.assign(`/loginscreen?${params.toString()}`);
}

/** Refresh the ID token when the tab becomes visible again. Redirect if the refresh token is dead. */
export function watchSessionExpiry(): void {
  if (typeof document === 'undefined') return;

  let lastCheck = 0;
  const check = () => {
    const user = auth.currentUser;
    if (!user) return;
    const now = Date.now();
    if (now - lastCheck < 60_000) return;
    lastCheck = now;
    void user.getIdToken(true).catch((err: unknown) => {
      if (isSessionExpiredError(err)) {
        void expireSessionAndGoToLogin();
      }
    });
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
}
